import React, {
  useContext, useReducer, useEffect, useState,
} from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  Container, Col, Row, Label, Alert,
} from 'reactstrap';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { Date32, User32, Check32 } from '@bphxd/ds-core-react/icons';
import { useMsal } from '@azure/msal-react';
import PropTypes from 'prop-types';
import AWSAppSyncClient from 'aws-appsync';
import ApolloClient from 'apollo-client';
import withClient from '../../hoc/withClient';
import PageHeading from '../../components/PageHeading/PageHeading';
import './_deliveryDocket.scss';
import ShipmentAddress from '../../components/ShipmentAddress/ShipmentAddress';
import LinkTile from '../../components/LinkTile/LinkTile';
import ContractIcon from '../../icons/ContractIcon';
import GasIcon from '../../icons/GasIcon';
import BackButton from '../../components/BackButton/BackButton';
import ProductsCard from '../../components/ProductsCard/ProductsCard';
import { useAppContext, DeliveryContext } from '../../state/GlobalState';
import {
  deliveryDocketState, deliverDocketReducer, DeliveryDocketActions,
  RedirectMode,
} from './DeliveryDocketReducer';
import { isDocketStatusDelivered, statuses } from '../../helpers/shipmentStatus';
import {
  isDocketShipmentAddressInValid, INSTRUCTION_RESET, skHasValue, isUnmappedOpenOrder,
} from '../../helpers/shipmentEngine';
import SignaturesFlow from './SignaturesFlow/SignaturesFlow';
import TruckIcon from '../../icons/TruckIcon';
import CheckListIcon from '../../icons/CheckListIcon';
import ConfirmActionModal from '../../components/ConfirmActionModal/ConfirmActionModal';
import { extractAttributeFromCollection } from '../../helpers/attributeExtraction';
import { getDeliveryDropDetails } from '../../api/graphQl/DropDetails';
import {
  ddBackButtonClickHandler, dropDetailTileClickHandler, signatureTileClickHandler,
  productDischargeAlertYesTracker, productDischargeAlertNoTracker,
} from '../../analytics/events';
import { getShipmentHeader } from '../../helpers/openOrders';
import { getShipmentDetails } from '../../api/graphQl/Shipment';
import pageTitleConstant from '../../analytics/constantPageTitle';
import { isLoadFullyDelivered } from '../../helpers/deliveryQuantities';

// Instructions should be shown only if none of the objects have value 'INSTRUCTION_RESET'
// DI is short for delivery_instruction and PI is short for permanent instruction
const showInstruction = (DI, PI) => !(DI === INSTRUCTION_RESET && PI === INSTRUCTION_RESET);

const DeliveryDocket = ({
  graphClient,
}) => {
  const history = useHistory();
  const {
    setSelectedDocket, shipmentId, handleGraphApiError, carrierId, virtualTerminal, isDPTerminal, tripType,
  } = useAppContext();
  const [deliveryId] = useContext(DeliveryContext);
  const [shipmentDetails, setShipmentDetails] = useState([]);
  const setShipmentDetailsForDelivery = (localShipmentDetails) => {
    const shipmentDetailsForDelivery = localShipmentDetails.filter(
      (sd) => skHasValue(sd, deliveryId),
    );
    setShipmentDetails(shipmentDetailsForDelivery);
  };

  if (!shipmentId || !deliveryId) {
    // If there's no shipment id or delivery id, redirect to find shipment page
    return <Redirect to="/shipment" />;
  }

  const username = useMsal().accounts[0].name;
  const [state, dispatch] = useReducer(deliverDocketReducer, deliveryDocketState);
  // store all drop details
  const [dropDetailsAll, setDropDetailsAll] = useState([]);
  const [lidStatus, setLidStatus] = useState(false);
  // Feature config is fetched on the landing page; fall back to the last known
  // value while offline so the flow can still be used without network.
  const signatureValidationEnabled = localStorage.getItem('signatureValidationEnabled') === 'true';

  const {
    deliveryDocket: currentDeliveryDocket,
    dropCount,
    isShippingAddressLoaded = true,
    redirectMode,
  } = state;

  const {
    PlannedStartDate,
    ShipmentAddress: address,
    LoadingPlant,
    SAPPlantId,
    OrderNo,
    PONumber,
    DeliveryComments,
    PermanentInstruction,
    Products,
    ReferenceId,
  } = currentDeliveryDocket;

  const isOpenOrder = isUnmappedOpenOrder(currentDeliveryDocket);

  // warning message details
  const lidWarningMessage = 'Product(s) loaded for this docket have not been fully delivered. Are you sure you want to continue?';
  const supportMessage = 'You will get a chance to redirect product(s) after collecting the signatures.';
  const [lidWarningStatus, setLidWarningStatus] = useState(false);
  const closeWindow = () => setLidWarningStatus(false);
  const continueSubmit = () => {
    setLidWarningStatus(false);
    if ((virtualTerminal || (isDPTerminal && tripType === 'O'))
        && ((redirectMode !== RedirectMode.PARTIAL && !deliveryId.includes("R"))
        || deliveryId.includes("R1"))) { // If the delivery docket is full redirect or is not a partial redirect, open BOL upload modal          
      dispatch({ type: DeliveryDocketActions.OPEN_BOL_UPLOAD_MODAL });
    } else {
      dispatch({ type: DeliveryDocketActions.OPEN_SIGNATURE_MODAL });
    }
    productDischargeAlertYesTracker();
  };

  const uniqLoadIdAddons = extractAttributeFromCollection(shipmentDetails, 'SK', 'LID#');

  const allDelivered = () => {
    if (isOpenOrder) {
      setLidStatus(true);
    } else if (uniqLoadIdAddons.length > 0) {
      let flag = true;
      uniqLoadIdAddons.forEach((uniqLoadIdAddon) => {
        flag = flag && isLoadFullyDelivered(uniqLoadIdAddon, shipmentDetails, dropDetailsAll);
      });
      setLidStatus(flag);
    }
  };

  const fetchAndSetAllDropDetails = async () => {
    try {
      const shDetails = await getShipmentDetails(graphClient, shipmentId, carrierId);
      if (shDetails?.data?.getShipmentDetails) {
        const allShipmentDetails = shDetails.data.getShipmentDetails;
        setShipmentDetailsForDelivery(allShipmentDetails);

        // The docket-level query returns drop details for every LID. Fetching it
        // once per LID duplicates the same drops and can incorrectly mark a
        // partially delivered docket as fully delivered.
        const matchingDeliveryDetail = allShipmentDetails.find(
          (sd) => sd.Status !== statuses.Void
            && sd.SK.split('#')[2].split('-')[0] === deliveryId.split('-')[0],
        );
        if (matchingDeliveryDetail) {
          const response = await getDeliveryDropDetails(graphClient, matchingDeliveryDetail);
          setDropDetailsAll(response?.data?.getDropDetails || []);
        }
      }
    } catch (error) {
      handleGraphApiError(error);
    }
  };

  useEffect(() => {
    // setting shipment details as well as all drop details
    fetchAndSetAllDropDetails();
    // setting initial state for shipment section
    dispatch({ type: DeliveryDocketActions.LOADING_SHIPMENT_ADDRESS });
  }, [shipmentId, deliveryId]);

  useEffect(() => {
    allDelivered();
  });

  // ensure browser "back" from this page always goes to shipment-details
  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      if (action === 'POP') {
        history.push('/shipment-details');
      }
    });
    return () => {
      unlisten();
    };
  }, [history]);

  let shipmentNoLabel;
  if(isDPTerminal && tripType === 'O') {
    shipmentNoLabel = 'External BOL:';
  } else if(isOpenOrder) {
    shipmentNoLabel = 'TAS Reference/BOL/Load ID:';
  } else {
    shipmentNoLabel = 'Shipment Number:';
  }
  const shipmentIdForLabel = isOpenOrder ? shipmentId.split('-')[0] : shipmentId;

  return (
    <Container data-testid="delivery-docket">
      <Row>
        <Col>
          <Link className="link" to="/shipment-details" onClick={ddBackButtonClickHandler(pageTitleConstant.deliveryDocketPage)}>
            <Row>
              <BackButton />
            </Row>
          </Link>

          <Row>
            <PageHeading label="Delivery Docket Number" refNumber={getShipmentHeader(deliveryId)} />
          </Row>

          <Row>
            {!isShippingAddressLoaded && (
              <Col sm="12" md="6" className="p-0">
                <Skeleton height="16px" width="90%" count={1} />
              </Col>
            )}
            {isShippingAddressLoaded && (
              <Col sm="12" md="6" className="pl-0 d-flex w-100 align-items-top mb-4">
                <Date32 className="date-icon" />
                <Label className="font-12" data-testid="delivery-date">
                  Delivery date:
                  {' '}
                  {PlannedStartDate}
                </Label>
              </Col>
            )}
            <Col sm="12" md="6" className="pl-0 d-flex w-100 align-items-top mb-4">
              <User32 className="driver-icon" />
              <Label className="font-12" data-testid="driver-name">
                Driver:
                {' '}
                {username}
              </Label>
            </Col>
          </Row>

          {currentDeliveryDocket && isDocketStatusDelivered(currentDeliveryDocket) && (
            <Row>
              <Alert
                key="0"
                className="d-flex align-items-center w-100 mb-4"
              >
                <Check32 />
                <p className="univers font-16">
                  Delivered - This is a read-only version and can’t be edited.
                </p>
              </Alert>
            </Row>
          )}

          <Row>
            <Col sm="12" md="6">
              <Row>
                <ShipmentAddress
                  address={address}
                  dispatch={dispatch}
                  docketDetails={currentDeliveryDocket}
                  dropCount={dropCount}
                  isLoaded={isShippingAddressLoaded}
                />
              </Row>
            </Col>

            <Col sm="12" md="6">
              <Row>
                <Col className="details-container-LP rounded mb-4" data-testid="loading-plant">
                  <Label className="univers mt-4 font-weight-light">Loading Plant: </Label>
                  <br />
                  {!isShippingAddressLoaded && (
                    <Col sm="12" md="6" className="p-0">
                      <Skeleton height="10%" width="90%" count={1} />
                      <Skeleton height="10%" width="70%" className="mb-4" count={1} />
                    </Col>
                  )}
                  {isShippingAddressLoaded && (
                    <Row>
                      <Col>
                        <Label className="font-13 m-0">
                          Name:
                          {' '}
                          {LoadingPlant}
                        </Label>
                        <br />
                        <Label className="font-13 mb-4">
                          Plant number:
                          {' '}
                          {SAPPlantId}
                        </Label>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>

              <Row>
                <Col className="details-container-RD rounded mb-4" data-testid="ref-details">
                  <Label className="univers mt-4 font-weight-light">Reference Details: </Label>
                  {!isShippingAddressLoaded && (
                    <Col sm="12" md="6" className="p-0">
                      <Skeleton height="10%" width="90%" count={1} />
                      <Skeleton height="10%" width="70%" count={1} />
                      <Skeleton height="10%" width="50%" className="mb-4" count={1} />
                    </Col>
                  )}
                  {isShippingAddressLoaded && (
                    <Row>
                      <Col>
                        {
                          isOpenOrder && (
                            <>
                              <Label className="font-13 m-0">
                                Order Type: Open
                              </Label>
                              <br />
                            </>
                          )
                        }
                        <Label className="font-13 m-0">
                          {shipmentNoLabel}
                          {' '}
                          {shipmentIdForLabel}
                        </Label>                        
                        <br />
                        {
                          isOpenOrder && (
                            <>
                              <Label className="font-13 m-0">
                                Reference ID:
                                {' '}
                                {ReferenceId}
                              </Label>
                              <br />
                            </>
                          )
                        }
                        <Label className="font-13 m-0">
                          SAP Order Number:
                          {' '}
                          {OrderNo}
                        </Label>
                        <br />
                        <Label className="font-13 mb-4">
                          Customer Purchase Order:
                          {' '}
                          {PONumber}
                        </Label>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            {!isShippingAddressLoaded && (
              <Col sm="12" className="instruction-box rounded pt-0 mb-6">
                <br />
                <Skeleton height="16px" width="50%" count={1} />
                <Skeleton height="16px" width="30%" className="mb-4" count={1} />
              </Col>
            )}
            {isShippingAddressLoaded && showInstruction(DeliveryComments, PermanentInstruction) && (
              <Col className="instruction-box rounded">
                <Row>
                  <Col sm="12" className="d-flex w-100 align-items-top mb-4 mt-6">
                    <TruckIcon />
                    <Label className="font-13 pl-1 mr-1">
                      Delivery Instruction:
                      {' '}
                      {DeliveryComments}
                    </Label>
                    <br />
                  </Col>
                  <Col sm="12" className="d-flex w-100 align-items-top mb-4">
                    <CheckListIcon />
                    <Label className="font-13 pl-1">
                      Permanent Instruction:
                      {' '}
                      {PermanentInstruction}
                    </Label>
                  </Col>
                </Row>
              </Col>
            )}
          </Row>

          <Row>
            <Col className="p-0">
              <LinkTile
                icon={GasIcon}
                title="Drop details"
                disabled={isDocketShipmentAddressInValid(currentDeliveryDocket)}
                onClick={() => (dropCount === 0
                  ? (setSelectedDocket(currentDeliveryDocket) || dropDetailTileClickHandler(pageTitleConstant.deliveryDocketPage)) || history.push('/enter-drop-details')
                  : (setSelectedDocket(currentDeliveryDocket) || dropDetailTileClickHandler(pageTitleConstant.deliveryDocketPage)) || history.push('/drop-details'))}
                testId="drop-details-link-tile"
              />
              <LinkTile
                icon={ContractIcon}
                title="Signatures"
                disabled={dropCount === 0 || isDocketStatusDelivered(currentDeliveryDocket)}
                onClick={
                  () => (
                    (lidStatus || isOpenOrder)
                      ? ((virtualTerminal || (isDPTerminal && tripType === 'O'))
                          && ((redirectMode !== RedirectMode.PARTIAL && !deliveryId.includes("R"))
                          || deliveryId.includes("R1")))
                        ? dispatch({ type: DeliveryDocketActions.OPEN_BOL_UPLOAD_MODAL })
                        : dispatch({ type: DeliveryDocketActions.OPEN_SIGNATURE_MODAL })
                      : setLidWarningStatus(true)
                  ) || signatureTileClickHandler(pageTitleConstant.deliveryDocketPage)
                }
                testId="signatures-link-tile"
              />
              <ConfirmActionModal
                data-testid="lid-warning-window"
                isOpen={lidWarningStatus}
                confirmMsg={lidWarningMessage}
                confirmSubMsg={supportMessage}
                handleCancel={() => { closeWindow(); productDischargeAlertNoTracker(); }}
                handleAction={continueSubmit}
              />
            </Col>
          </Row>

          {!virtualTerminal && 
            Products && Products.length > 0 && (
              <Row>
                <ProductsCard
                  shipmentDetails={shipmentDetails}
                />
              </Row>
            )
          }
          {virtualTerminal && !deliveryId.includes("R") && 
            Products && Products.length > 0 && (
              <Row>
                <ProductsCard
                  shipmentDetails={shipmentDetails}
                />
              </Row>
            )
          }
        </Col>
      </Row>

      <SignaturesFlow
        state={state}
        dispatch={dispatch}
        cleared={lidStatus && !isOpenOrder}
        signatureValidationEnabled={signatureValidationEnabled}
      />
    </Container>
  );
};

/**
 * We use oneOfType because
 * the application uses AppSyncClient
 * the tests/jest provide ApolloClient as we use the <MockProvider/>
 */
DeliveryDocket.propTypes = {
  graphClient: PropTypes.oneOfType([
    PropTypes.instanceOf(AWSAppSyncClient).isRequired,
    PropTypes.instanceOf(ApolloClient).isRequired,
  ]).isRequired,
};

// withClient will provide the client in props
export default withClient(DeliveryDocket);
